import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { userApi } from '../api/client';
import { useApiQuery } from '../hooks/useApiQuery';
import { useAuthStore } from '../stores/authStore';
import { MALAYSIAN_STATES } from '../lib/constants';
import { Button } from './ui';

export function StatePromptModal() {
  const { updateUser } = useAuthStore();
  const queryClient = useQueryClient();
  const [state, setState] = useState('');

  const { data: profile } = useApiQuery({
    queryKey: ['profile'],
    queryFn: () => userApi.getProfile(),
    refetchOnMount: 'always',
  });

  const mutation = useMutation({
    mutationFn: (newState: string) => userApi.updateProfile({ state: newState }),
    onSuccess: (response) => {
      if (response.error) { toast.error(response.error); return; }
      if (response.data) {
        updateUser(response.data);
        queryClient.invalidateQueries({ queryKey: ['profile'] });
        toast.success('State saved');
      }
    },
  });

  // Only block when the profile has loaded and no state is set yet.
  if (!profile || profile.state) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Complete your profile</h2>
          <p className="mt-1 text-sm text-gray-600">
            Please select your state so we can match you with relevant tasks. This is required to continue.
          </p>
        </div>
        <div>
          <label className="label">State</label>
          <select
            value={state}
            onChange={(e) => setState(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">Select your state…</option>
            {MALAYSIAN_STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={() => mutation.mutate(state)}
            loading={mutation.isPending}
            disabled={!state}
          >
            Save and continue
          </Button>
        </div>
      </div>
    </div>
  );
}
